// Shared ticket layout model used by both the HTML preview and the ESC/POS (RawBT) printer.

export const TEXT_SIZES = [
  { value: "normal", label: "Normal" },
  { value: "tall", label: "Alto" },
  { value: "wide", label: "Ancho" },
  { value: "big", label: "Grande" },
];

export const RECEIPT_TEXT_ELEMENTS = [
  { key: "business", label: "Nombre de la banca", def: { size: "tall", bold: true } },
  { key: "slogan", label: "Eslogan", def: { size: "normal", bold: false } },
  { key: "meta", label: "Datos (Fecha, Serial, Ticket, Código)", def: { size: "normal", bold: false } },
  { key: "title", label: "Título (VENTA DE LOTERIA)", def: { size: "normal", bold: false } },
  { key: "legend", label: "Leyenda de loterías", def: { size: "tall", bold: false } },
  { key: "header", label: "Encabezado de tabla", def: { size: "tall", bold: false } },
  { key: "plays", label: "Jugadas", def: { size: "tall", bold: false } },
  { key: "total", label: "Total", def: { size: "big", bold: true } },
  { key: "footer", label: "Textos del pie", def: { size: "normal", bold: false } },
];

export function resolveReceiptStyles(settings = {}) {
  const saved = settings.receipt_styles || {};
  return Object.fromEntries(RECEIPT_TEXT_ELEMENTS.map((e) => [e.key, { ...e.def, ...(saved[e.key] || {}) }]));
}

const MONTHS = ["ene", "feb", "mar", "abr", "may", "jun", "jul", "ago", "sep", "oct", "nov", "dic"];

const PRODUCT_ABBR = {
  QUINIELA: "QL", PALE: "PL", TRIPLETA: "TP", SUPERPALE: "SP",
  PEGA3: "P3", PEGA4: "P4", LOTO: "LT",
};

const norm = (s) => String(s || "").normalize("NFD").replace(/[\u0300-\u036f]/g, "").toUpperCase().replace(/[^A-Z0-9]/g, "");

export function productAbbr(item) {
  const key = norm(item.play_type_code) || norm(item.play_type_name);
  return PRODUCT_ABBR[key] || key.slice(0, 2) || "--";
}

// "LEIDSA Noche" -> "Ln", "Florida Medio Día" -> "Fmd"
export function autoLotteryAbbr(name) {
  const words = String(name || "").normalize("NFD").replace(/[\u0300-\u036f]/g, "").split(/\s+/).filter(Boolean);
  if (words.length === 0) return "--";
  if (words.length === 1) return words[0].slice(0, 1).toUpperCase() + words[0].slice(1, 2).toLowerCase();
  return (words[0][0].toUpperCase() + words.slice(1).map((w) => w[0].toLowerCase()).join("")).slice(0, 3);
}

export function formatTicketDate(iso) {
  const d = new Date(iso || Date.now());
  const p = (n) => String(n).padStart(2, "0");
  const h = d.getHours();
  const h12 = ((h + 11) % 12) + 1;
  return `${p(d.getDate())}/${MONTHS[d.getMonth()]}./${String(d.getFullYear()).slice(-2)} ${p(h12)}:${p(d.getMinutes())}:${p(d.getSeconds())} ${h >= 12 ? "PM" : "AM"}`;
}

export function formatMoney(n) {
  const v = Number(n || 0);
  const isInt = Math.abs(v - Math.round(v)) < 0.005;
  return `RD$ ${v.toLocaleString("en-US", { minimumFractionDigits: isInt ? 0 : 2, maximumFractionDigits: isInt ? 0 : 2 })}`;
}

// Deterministic 10-digit verification code derived from ticket data.
export function verificationCode(ticket) {
  const src = `${ticket?.ticket_number}|${ticket?.created_at}|${ticket?.total_amount}|${(ticket?.items || []).length}`;
  let h1 = 0x811c9dc5, h2 = 0x1505;
  for (let i = 0; i < src.length; i++) {
    const c = src.charCodeAt(i);
    h1 = Math.imul(h1 ^ c, 0x01000193) >>> 0;
    h2 = ((h2 * 33) ^ c) >>> 0;
  }
  return String((h1 % 100000) * 100000 + (h2 % 100000)).padStart(10, "0");
}

export function buildTicketModel(ticket, settings = {}) {
  const items = ticket?.items || [];
  const abbrByGame = new Map();
  const used = new Set();
  for (const it of items) {
    const name = it.game_name || "";
    if (abbrByGame.has(name)) continue;
    const base = (it.game_short_code || "").trim() || autoLotteryAbbr(name);
    let abbr = base;
    for (let n = 2; used.has(abbr); n++) abbr = `${base}${n}`;
    used.add(abbr);
    abbrByGame.set(name, abbr);
  }
  const serialUser = ticket?.user_username || String(ticket?.user_name || "").split("@")[0];
  const on = (k) => settings[k] ?? true;
  return {
    styles: resolveReceiptStyles(settings),
    showLogo: on("receipt_show_logo"),
    showQr: on("receipt_show_qr"),
    qrSize: settings.receipt_qr_size || "medium",
    logoSize: settings.receipt_logo_size || "medium",
    title: settings.receipt_title_text ?? "VENTA DE LOTERIA",
    bizName: (settings.receipt_business_name || "BANCA POS").toUpperCase(),
    slogan: settings.receipt_footer_line1 || settings.receipt_footer || "",
    fecha: formatTicketDate(ticket?.created_at),
    serial: on("receipt_show_serial") ? [ticket?.branch_code, serialUser].filter(Boolean).join("-") : "",
    ticketNo: String(ticket?.ticket_number || ""),
    codeNo: on("receipt_show_code") ? verificationCode(ticket) : "",
    legend: on("receipt_show_legend") ? [...abbrByGame.entries()].map(([name, abbr]) => ({ abbr, name })) : [],
    rows: items.map((it) => ({
      key: it.id,
      prod: productAbbr(it),
      num: String(it.display || (it.numbers || []).join("-")),
      lot: abbrByGame.get(it.game_name || ""),
      amt: formatMoney(it.amount),
    })),
    total: formatMoney(ticket?.total_amount),
    bottomLines: [settings.receipt_footer_line2 || "REVISE SU TICKET", settings.receipt_footer_line3, (settings.receipt_website || "").toUpperCase()].filter(Boolean),
  };
}
